import { useDispatch, useSelector } from 'react-redux'
import { Analytics, CustomEventTypes } from '@src/config'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import { CompleteLiveNotificationMutation } from '@src/graphql/completeLiveNotification.generated'
import { DismissLiveNotificationMutation } from '@src/graphql/dismissLiveNotification.generated'
import { LiveNotificationRecord } from '@src/models/notifications/types'
import { userSelector } from '@src/selectors/app'
import { NotificationEngineNotificationActionKind, NotificationEngineScreen } from '@src/types'
import { handleEngineNotification } from '@src/components/notifications/utils/handler'
import { useLiveNotifications } from './useLiveNotifications'

interface UseLiveNotificationBannerOptions {
  screen: NotificationEngineScreen
}

export interface LiveNotificationBannerData {
  action?: () => void
  actionButtonLabel: string
  dismiss?: () => void
  dismissButtonLabel: string
  notification: LiveNotificationRecord
}

type CompleteLiveNotificationResponse = CompleteLiveNotificationMutation['completeLiveNotification']
type DismissLiveNotificationResponse = DismissLiveNotificationMutation['dismissLiveNotification']

export const useLiveNotificationBanner = ({
  screen,
}: UseLiveNotificationBannerOptions): LiveNotificationBannerData | null => {
  const notificationInboxEnabled = useFeatureFlag(Feature.NotificationInbox)
  const dispatch = useDispatch()
  const user = useSelector(userSelector)
  const notifications = useLiveNotifications({ screen })
  const liveNotification = notifications[0] as LiveNotificationRecord | undefined

  const liveSurfaceEnabledForScreen =
    notificationInboxEnabled || screen === NotificationEngineScreen.Inbox

  if (!liveSurfaceEnabledForScreen || !liveNotification || !user) {
    return null
  }

  const persistedNotification = liveNotification.notification
  const hasAction = liveNotification.actionKind !== NotificationEngineNotificationActionKind.None

  const notificationData = {
    actionKind: liveNotification.actionKind,
    ...(persistedNotification
      ? { id: persistedNotification.id, kind: persistedNotification.kind }
      : {}),
    ...(liveNotification.payload || {}),
  }

  const action = hasAction
    ? () => {
        handleEngineNotification({
          notificationData,
          userId: user.id,
          dispatch,
          completeOnInteraction: false,
          markReadOnInteraction: false,
        })

        dispatch({
          type: 'notifications/completeLive',
          payload: { id: liveNotification.id },
          success: (nextLiveNotification: CompleteLiveNotificationResponse) => {
            Analytics.track(CustomEventTypes.NotificationLiveSurfaceCompleted, {
              ...notificationData,
              id: liveNotification.id,
              screen,
              userId: user.id,
            })

            const nextNotification = nextLiveNotification.notification

            if (!nextNotification) {
              return
            }

            // Event for legacy notifications (pre-inbox experience)
            Analytics.track(CustomEventTypes.PushNotificationTapped, {
              ...notificationData,
              userId: user.id,
              dateSent: nextNotification.createdAt,
            })
          },
        })
      }
    : undefined

  const dismiss = liveNotification.dismissible
    ? () => {
        dispatch({
          type: 'notifications/dismissLive',
          payload: { id: liveNotification.id },
          success: (nextLiveNotification: DismissLiveNotificationResponse) => {
            Analytics.track(CustomEventTypes.NotificationLiveSurfaceDismissed, {
              ...notificationData,
              id: liveNotification.id,
              screen,
              userId: user.id,
            })

            const nextNotification = nextLiveNotification.notification

            if (!nextNotification) {
              return
            }

            // Event for legacy notifications (pre-inbox experience)
            Analytics.track(CustomEventTypes.NotificationBannerDismissed, {
              id: nextNotification.id,
              actionKind: nextNotification.actionKind,
              kind: nextNotification.kind,
              ...nextNotification.payload,
              screen,
              userId: user.id,
              dateSent: nextNotification.createdAt,
            })
          },
        })
      }
    : undefined

  return {
    action,
    actionButtonLabel: liveNotification.actionButtonLabel,
    dismissButtonLabel: liveNotification.dismissButtonLabel,
    notification: liveNotification,
    dismiss,
  }
}
